import { useEffect, useRef, useState } from "react";
import { Mic, MicOff, Video, VideoOff, Settings, Check } from "lucide-react";

// The doctor's call controls for a Jitsi visit: mic, camera and device
// settings, nothing else (Jitsi's own toolbar is hidden). Driven through the
// External API. The button state is read back from Jitsi after every tap and
// every few seconds, because on the live server Jitsi's mute events don't
// always arrive.
const DEVICE_GROUPS = [
  { key: "audioInput", label: "Microphone", set: "setAudioInputDevice" },
  { key: "videoInput", label: "Camera", set: "setVideoInputDevice", video: true },
  { key: "audioOutput", label: "Speaker", set: "setAudioOutputDevice" },
];

export default function JitsiCallControls({ api, audioOnly }) {
  const [micMuted, setMicMuted] = useState(false);
  const [camMuted, setCamMuted] = useState(!!audioOnly);
  const [deviceError, setDeviceError] = useState(""); // "" | "mic" | "camera"
  const [showSettings, setShowSettings] = useState(false);
  const [devices, setDevices] = useState(null);
  const [current, setCurrent] = useState({});
  const panelRef = useRef(null);

  const sync = () => {
    if (!api) return;
    api.isAudioMuted?.().then(setMicMuted).catch(() => {});
    api.isVideoMuted?.().then(setCamMuted).catch(() => {});
  };

  useEffect(() => {
    if (!api) return undefined;
    const onAudio = ({ muted }) => setMicMuted(muted);
    const onVideo = ({ muted }) => setCamMuted(muted);
    const onMicError = () => setDeviceError("mic");
    const onCamError = () => setDeviceError("camera");
    api.on("audioMuteStatusChanged", onAudio);
    api.on("videoMuteStatusChanged", onVideo);
    api.on("micError", onMicError);
    api.on("cameraError", onCamError);
    sync();
    const t = setInterval(sync, 4000);
    return () => {
      clearInterval(t);
      try {
        api.off?.("audioMuteStatusChanged", onAudio);
        api.off?.("videoMuteStatusChanged", onVideo);
        api.off?.("micError", onMicError);
        api.off?.("cameraError", onCamError);
      } catch (e) { /* call already closed */ }
    };
  }, [api]);

  const command = (cmd) => {
    setDeviceError("");
    try { api?.executeCommand(cmd); } catch (e) { /* call already closed */ }
    setTimeout(sync, 800);
    setTimeout(sync, 2500);
  };

  const loadDevices = async () => {
    try {
      const [available, inUse] = await Promise.all([api.getAvailableDevices(), api.getCurrentDevices()]);
      setDevices(available || {});
      setCurrent(inUse || {});
    } catch (e) {
      setDevices({});
    }
  };

  const toggleSettings = () => {
    const next = !showSettings;
    setShowSettings(next);
    if (next) loadDevices();
  };

  const pick = (group, device) => {
    try { api[group.set](device.label, device.deviceId); } catch (e) { /* ignore */ }
    setCurrent((c) => ({ ...c, [group.key]: device }));
  };

  // Close the settings panel on an outside click.
  useEffect(() => {
    if (!showSettings) return undefined;
    const onDown = (e) => { if (panelRef.current && !panelRef.current.contains(e.target)) setShowSettings(false); };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [showSettings]);

  const btn = (active) => `w-12 h-12 rounded-full flex items-center justify-center transition active:scale-95 ${
    active ? "bg-rose-600 text-white" : "bg-white/15 text-white hover:bg-white/25"
  }`;

  return (
    <>
      {deviceError && (
        <div role="alert" className="absolute inset-x-3 bottom-24 z-30 mx-auto max-w-md rounded-2xl bg-white p-3.5 shadow-2xl border border-rose-200 text-sm text-secondary-700">
          <p className="font-bold text-rose-700">
            {deviceError === "camera" ? "Your camera couldn't be turned on" : "Your microphone couldn't be turned on"}
          </p>
          <p className="mt-1 text-xs leading-relaxed">
            The browser blocked it, or another app is using it. Allow it from the lock icon next to the web address, close other apps using it, then try again.
          </p>
          <button type="button" onClick={() => setDeviceError("")} className="mt-2 text-xs font-bold text-primary-700">OK</button>
        </div>
      )}
      <div className="absolute inset-x-0 bottom-0 z-30 flex justify-center pb-4 pointer-events-none">
        <div ref={panelRef} className="relative pointer-events-auto flex items-center gap-3 rounded-full bg-secondary-900/80 backdrop-blur-md px-3 py-2 shadow-2xl">
          <button type="button" onClick={() => command("toggleAudio")} aria-pressed={micMuted}
            aria-label={micMuted ? "Turn microphone on" : "Turn microphone off"} title={micMuted ? "Turn microphone on" : "Turn microphone off"} className={btn(micMuted)}>
            {micMuted ? <MicOff size={20} /> : <Mic size={20} />}
          </button>
          {!audioOnly && (
            <button type="button" onClick={() => command("toggleVideo")} aria-pressed={camMuted}
              aria-label={camMuted ? "Turn camera on" : "Turn camera off"} title={camMuted ? "Turn camera on" : "Turn camera off"} className={btn(camMuted)}>
              {camMuted ? <VideoOff size={20} /> : <Video size={20} />}
            </button>
          )}
          <button type="button" onClick={toggleSettings} aria-expanded={showSettings} aria-label="Device settings" title="Device settings" className={btn(false)}>
            <Settings size={20} />
          </button>

          {showSettings && (
            <div className="absolute bottom-full right-0 mb-3 w-72 max-h-[60vh] overflow-y-auto rounded-2xl bg-white p-3 shadow-2xl border border-secondary-100 text-sm">
              {!devices && <p className="text-xs text-secondary-500 p-2">Loading devices…</p>}
              {devices && DEVICE_GROUPS.filter((g) => !(g.video && audioOnly)).map((g) => {
                const list = devices[g.key] || [];
                return (
                  <div key={g.key} className="mb-2 last:mb-0">
                    <p className="px-2 pb-1 text-[11px] font-semibold uppercase tracking-wide text-secondary-400">{g.label}</p>
                    {list.length === 0 && <p className="px-2 text-xs text-secondary-500">None found</p>}
                    {list.map((d) => {
                      const selected = current[g.key]?.deviceId === d.deviceId;
                      return (
                        <button key={d.deviceId || d.label} type="button" onClick={() => pick(g, d)}
                          className={`flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-left text-xs ${selected ? "bg-primary-50 text-primary-800 font-semibold" : "text-secondary-700 hover:bg-secondary-50"}`}>
                          <span className="w-4 shrink-0">{selected && <Check size={14} />}</span>
                          <span className="truncate">{d.label || "Unnamed device"}</span>
                        </button>
                      );
                    })}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </>
  );
}
